/**
 * Aging report + Expenses page.
 * Two pages in one file to keep the module count tidy.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Plus, X, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { booksClient, getBooksTenant, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const fmt = (n, cur) => {
  const sym = { GBP: '£', INR: '₹', MYR: 'RM ', KZT: '₸', USD: '$', EUR: '€' }[cur] || '';
  return `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ---------------- Aging ----------------
export const AgingPage = () => {
  const loc = useLocation();
  const kind = loc.pathname.includes('payables') ? 'bill' : 'invoice';
  const label = kind === 'invoice' ? 'Debtors Aging (AR)' : 'Creditors Aging (AP)';
  const [d, setD] = useState(null);
  const cur = getBooksTenant()?.base_currency;

  useEffect(() => {
    booksClient.get('/documents/reports/aging', { params: { kind } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [kind]);

  if (!d) return <><H eyebrow="Report" title={label} /><Loading /></>;

  return (
    <div>
      <H eyebrow="Report" title={label} />
      <div className="text-xs text-[#0b1a2e]/60 mb-3">As of {d.as_of}</div>
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm" data-testid="aging-table">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
            <tr>
              <th className="text-left px-4 py-2">{kind === 'invoice' ? 'Customer' : 'Vendor'}</th>
              <th className="text-right px-4 py-2 w-24">Current</th>
              <th className="text-right px-4 py-2 w-24">1–30</th>
              <th className="text-right px-4 py-2 w-24">31–60</th>
              <th className="text-right px-4 py-2 w-24">61–90</th>
              <th className="text-right px-4 py-2 w-24">90+</th>
              <th className="text-right px-4 py-2 w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-[#0b1a2e]/40 italic">No outstanding balances.</td></tr>
            ) : d.rows.map((r) => (
              <tr key={r.contact_id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium text-[#0b1a2e]">{r.contact_name}</td>
                <td className="px-4 py-2 text-right font-mono">{fmt(r.current, cur)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmt(r['1_30'], cur)}</td>
                <td className="px-4 py-2 text-right font-mono text-amber-700">{fmt(r['31_60'], cur)}</td>
                <td className="px-4 py-2 text-right font-mono text-orange-700">{fmt(r['61_90'], cur)}</td>
                <td className="px-4 py-2 text-right font-mono text-red-700">{fmt(r['90_plus'], cur)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmt(r.total, cur)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#0f5c3d] font-semibold">
              <td className="px-4 py-2 text-right">Totals</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.totals.current, cur)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.totals['1_30'], cur)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.totals['31_60'], cur)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.totals['61_90'], cur)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.totals['90_plus'], cur)}</td>
              <td className="px-4 py-2 text-right font-mono text-[#0f5c3d]">{fmt(d.totals.total, cur)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ---------------- Expenses ----------------
export const ExpensesPage = () => {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const cur = getBooksTenant()?.base_currency;

  const load = () => {
    setLoading(true);
    Promise.all([
      booksClient.get('/expenses'),
      booksClient.get('/ledger/accounts', { params: { type: 'expense' } }),
    ]).then(([e, a]) => { setItems(e.data.data || []); setAccounts(a.data.data || []); })
    .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const approve = async (id) => {
    try { await booksClient.post(`/expenses/${id}/approve`); toast({ title: 'Approved — journal posted' }); load(); }
    catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?');
    if (reason === null) return;
    try { await booksClient.post(`/expenses/${id}/reject`, { reason }); toast({ title: 'Rejected' }); load(); }
    catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Purchases</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Expense Claims</h1>
        </div>
        <button onClick={() => setOpenNew(true)} data-testid="new-expense-btn"
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
          <Plus className="w-4 h-4" /> New claim
        </button>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
      items.length === 0 ? (
        <div className="p-12 text-center text-[#0b1a2e]/40 italic bg-white rounded-2xl border border-black/5">No expense claims yet.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Submitter</th>
                <th className="text-left px-4 py-2">Submitted</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.expense_id} className="border-t border-slate-100 hover:bg-[#f7fbf8]">
                  <td className="px-4 py-2 text-[#0b1a2e] font-medium">{e.title}</td>
                  <td className="px-4 py-2 text-[#0b1a2e]/70">{e.submitter_name}</td>
                  <td className="px-4 py-2 text-xs text-[#0b1a2e]/60">{String(e.created_at).slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(e.total_amount, cur)}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
                      e.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                      e.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}>{e.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    {e.status === 'pending' && (
                      <>
                        <button onClick={() => approve(e.expense_id)} title="Approve" data-testid={`exp-approve-${e.expense_id}`}
                          className="text-emerald-700 hover:text-emerald-900"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => reject(e.expense_id)} title="Reject" data-testid={`exp-reject-${e.expense_id}`}
                          className="text-red-600 hover:text-red-800"><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openNew && (
        <NewExpense accounts={accounts} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />
      )}
    </div>
  );
};

const NewExpense = ({ accounts, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title: '', notes: '',
    lines: [{ date: today, description: '', account_id: '', amount: 0 }],
  });
  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { date: today, description: '', account_id: '', amount: 0 }] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    try {
      await booksClient.post('/expenses', {
        ...form,
        lines: form.lines.map((l) => ({ ...l, amount: parseFloat(l.amount) || 0 })),
      });
      toast({ title: 'Claim submitted' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] overflow-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-medium text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>New Expense Claim</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <F label="Title *"><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="exp-title" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
          <F label="Notes"><input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
        </div>
        <table className="w-full text-sm mb-3">
          <thead className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
            <tr>
              <th className="text-left py-1 w-32">Date</th>
              <th className="text-left py-1">Description</th>
              <th className="text-left py-1 w-40">Category</th>
              <th className="text-right py-1 w-28">Amount</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {form.lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1 pr-2"><input type="date" value={l.date} onChange={(e) => setLine(i, { date: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" /></td>
                <td className="py-1 pr-2"><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" /></td>
                <td className="py-1 pr-2">
                  <select value={l.account_id} onChange={(e) => setLine(i, { account_id: e.target.value })} data-testid={`exp-line-${i}-account`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs">
                    <option value="">Select…</option>
                    {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}
                  </select>
                </td>
                <td className="py-1 pr-2"><input type="number" step="0.01" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} data-testid={`exp-line-${i}-amount`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" /></td>
                <td>{form.lines.length > 1 && <button onClick={() => rmLine(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addLine} className="text-xs text-[#0f5c3d] font-semibold">+ Add line</button>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.title || form.lines.some((l) => !l.description || !l.account_id || !parseFloat(l.amount))}
            data-testid="exp-submit"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">Submit for approval</button>
        </div>
      </div>
    </div>
  );
};

const Loading = () => <div className="p-12 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>;
const H = ({ eyebrow, title }) => (
  <div className="mb-6">
    <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">{eyebrow}</div>
    <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
  </div>
);
const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);
